import styled from 'libs/styled';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';

import Column from '@sportson/core-web/App/layouts/Column';
import Row from '@sportson/core-web/App/layouts/Row';
import Text from 'components/Text';

const Div = styled('div')``;
const Span = styled('span')``;

const VariantButton = styled('button')`
    display: flex;
    min-width: 48px;
    justify-content: center;
    align-items: center;
    padding: 14px 16px;

    color: var(--text-color-default);
    border-radius: var(--corner-radius-round, 4px);
    background: var(--color-neutrals-100);
    border: 1px solid transparent;
    transition: all var(--transition-primary-slow);

    &:hover {
        border-radius: var(--corner-radius-round, 4px);
        border: 1px solid var(--color-neutrals-300);
    }

    ${({ selected }) =>
        selected &&
        `
        border-radius: var(--corner-radius-round, 4px);
        border: 1px solid var(--color-base-black, #1b1b1b);
        `}

    ${({ outOfStock }) =>
        outOfStock &&
        `
        border-radius: var(--corner-radius-round, 4px);
        border: 1px solid var(--color-neutrals-300, #C0C0C0);
        `}

        &.disabled-btn {
        border: 1px solid var(--color-neutrals-300);
        background: linear-gradient(
            to top right,
            var(--color-neutrals-100) calc(50% - 1px),
            var(--color-neutrals-300),
            var(--color-neutrals-100) calc(50% + 1px)
        );

        pointer-events: none;
    }
`;

const VariantDisplayWrapper = styled('div')`
    display: flex;
    height: 40px;
    max-height: 40px;
    min-width: 48px;
    justify-content: center;
    align-items: center;

    border-radius: var(--corner-radius-round, 4px);
    border: 1px solid var(--color-base-black);
    background: var(--color-neutrals-100);
`;

const ColorIndicator = styled(Div)`
    height: 16px;
    width: 16px;

    border-radius: 100px;
    border: 1px solid var(--color-neutrals-200);
`;

const VariantText = styled(Text)`
    &.is-disabled {
        color: var(--color-neutrals-500);
    }
`;

const ProductVariants = ({
    variants,
    selectedParametrics,
    selectParametricValue,
    singleParametricSelection,
    checkPossibleSelection,
}) => {
    const [colorParametric, setColorParametric] = useState({
        name: '',
        value: '',
        valueDescription: '',
    });

    const selectedProductColorParametric = (singleParametricSelection) => {
        const filteredValues = singleParametricSelection?.filter((x) => x !== undefined);
        let colorParametric = {};
        filteredValues?.map((parametric) => {
            if (parametric.code === 'färg') {
                colorParametric = {
                    ...colorParametric,
                    name: parametric.name,
                    valueDescription: parametric.valueDescription,
                    value: parametric.value,
                };
            }
            if (parametric.code === 'färg teknisk' && !colorParametric.value) {
                colorParametric = {
                    ...colorParametric,
                    value: parametric.value,
                };
            }
        });
        return colorParametric;
    };

    useEffect(() => {
        if (singleParametricSelection) {
            setColorParametric(selectedProductColorParametric(singleParametricSelection));
        }
    }, [singleParametricSelection]);

    if (variants) {
        variants = variants.map((variant, index) => {
            const { values, code } = variant;

            return {
                ...variant,
                values: values.reduce((result, val) => {
                    if (!val.value) {
                        return result;
                    }

                    if (result.every((r) => r.value !== val.value)) {
                        result.push(val);
                    }

                    return result;
                }, []),
                index,
            };
        });
    }

    return (
        <Column my="16px" gap="24px">
            {singleParametricSelection && (
                <Column gap="24px">
                    {colorParametric?.name && (
                        <Column gap="8px">
                            <Text typography="UI/14_100_0_450">
                                <Span marginRight="8px">{`${colorParametric.name}:`}</Span>
                                <Span color="var(--color-neutrals-500)">{colorParametric.value}</Span>
                            </Text>
                            <Row gap="8px">
                                <VariantDisplayWrapper p="12px 16px">
                                    <ColorIndicator backgroundColor={colorParametric.valueDescription} />
                                </VariantDisplayWrapper>
                            </Row>
                        </Column>
                    )}
                </Column>
            )}

            {variants?.map(({ id, code, name, values }, parametricIndex) => {
                const isColorParmetric = code === 'färg teknisk';
                const selectedValue = (selectedParametrics[parametricIndex] || {}).value;
                const colorLabelSuffix = selectedValue ? `${selectedValue}` : '';
                // Hackfix to make giftcard variants clickable even if they are not in stock.
                // And they won't have stock since they are virtual products.
                const isGiftcard = code === 'belopp';

                return (
                    <Column key={`pdp-variant-${code}`} gap="8px">
                        {isColorParmetric ? (
                            <Text typography="UI/14_100_0_450">
                                <Span marginRight="8px">{name}</Span>
                                <Span color="var(--color-neutrals-500)">{colorLabelSuffix}</Span>
                            </Text>
                        ) : (
                            <Text typography="UI/14_100_0_450">
                                <Span marginRight="8px">{`${name}:`}</Span>
                                <Span color="var(--color-neutrals-500)">{selectedValue}</Span>
                            </Text>
                        )}
                        <Row gap="4px" flexWrap="wrap">
                            {values.map(({ label, valueDescription, value }) => {
                                const isClickable = isGiftcard || checkPossibleSelection(parametricIndex, id, label);

                                return (
                                    <Fragment key={label}>
                                        <VariantButton
                                            className={!isClickable ? 'disabled-btn' : ''}
                                            p={isColorParmetric ? '12px 16px' : '14px 16px'}
                                            onClick={() => selectParametricValue(parametricIndex, value)}
                                            selected={selectedValue === value}
                                            label={label}
                                        >
                                            {isColorParmetric && valueDescription ? (
                                                <ColorIndicator backgroundColor={valueDescription} />
                                            ) : (
                                                <VariantText
                                                    className={!isClickable ? 'is-disabled' : ''}
                                                    typography="UI/14_100_0_450"
                                                >
                                                    {label}
                                                </VariantText>
                                            )}
                                        </VariantButton>
                                    </Fragment>
                                );
                            })}
                        </Row>
                    </Column>
                );
            })}
        </Column>
    );
};

ProductVariants.propTypes = {
    layout: PropTypes.string,
    selectedParametrics: PropTypes.array,
    variants: PropTypes.array,
    selectParametricValue: PropTypes.object,
};

export default ProductVariants;
